/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useMemo } from 'react';
import Styles from '../../css/BorrowerSelectLoans.module.css';
import { createTheme, ThemeProvider, CircularProgress } from '@mui/material';
import LoadingModal from '../modals/LoadingModal';
import {
  addMonthsYrsToTimestamp,
  epochToDate,
  formatDate,
  getLpiDueDateString,
  isObjEmptyOrNull,
  isStrEmptyOrNull,
} from '../../common/UtilityFunctions';
import { useNavigationAlert } from '../../contexts/NavigationAlertContext';
import { useLoanContext } from '../../contexts/LoanContext';
import { useLoginContext } from '../../contexts/LoginContext';
import { BatchEndpoint, Status, useBatchEditable } from '../../common/hooks/useBatchEditable';
import { NumericFormat } from 'react-number-format';
import { UseGetServicingInstallments } from '../../common/hooks/useGetServicingInstallments';
import { useLoanSearchQuery } from '../../common/hooks/useLoanSearchQuery';

const theme = createTheme({
  palette: {
    text: {
      disabled: 'dimgray',
    },
  },
  typography: {
    fontFamily: `"Poppins", sans-serif`,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
  },
  components: {
    MuiSelect: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
  },
});

type BatchLoanResp = {
  [key: string]: LoanData;
};

type BatchInstallmentResp = {
  [key: string]: UseGetServicingInstallments;
};

const BorrowerSelectLoans = () => {
  const { setLoanNumber } = useLoanContext();
  const { emailAddress: email } = useLoginContext();
  const { handleNavigation } = useNavigationAlert();

  const { loans, loading, errors } = useLoanSearchQuery({
    brwrEmail: email,
    includeAllBorrowers: true,
    rcsServicedOnly: true,
  });

  const noDuplicateLoans = useMemo(() => [...new Map(loans.map((item) => [item.loanNo, item])).values()], [loans]);

  const getBatchLoansObj = useCallback(() => {
    if (noDuplicateLoans.length < 1) return null;
    const batchEndpoints: BatchEndpoint[] = [];
    noDuplicateLoans.forEach((loanInfo) => {
      batchEndpoints.push({
        endpointName: loanInfo.loanNo,
        path: (process.env.REACT_APP_LOAN || '')
          .replace('live/', '')
          .replace('?', encodeURIComponent(loanInfo?.loanNo ? loanInfo.loanNo : '')),
        requestTypes: ['GET'],
      });
    });
    return batchEndpoints;
  }, [noDuplicateLoans]);

  const batchLoansObj = useMemo(() => getBatchLoansObj(), [getBatchLoansObj]);

  const { curr: loansBatch, status: loansBatchStatus } = useBatchEditable<BatchLoanResp>({
    batchEndpoints: batchLoansObj,
  });

  const getBatchInstallmentsObj = useCallback(() => {
    if (isObjEmptyOrNull(loansBatch) || loansBatchStatus == Status.LOADING) return null;
    const batchEndpoints: BatchEndpoint[] = [];
    for (const [loannumber, loandata] of Object.entries(loansBatch)) {
      batchEndpoints.push({
        endpointName: loannumber,
        path:
          (process.env.REACT_APP_INSTALLMENTS_DUEDATE || '')
            .replace('live/', '')
            .replace('?', encodeURIComponent(loannumber)) +
          encodeURIComponent(getLpiDueDateString(loandata?.lpi ? loandata.lpi : 0)),
        requestTypes: ['GET'],
      });
    }
    return batchEndpoints;
  }, [loansBatch, loansBatchStatus]);

  const batchInstallmentsObj = useMemo(() => getBatchInstallmentsObj(), [getBatchInstallmentsObj]);

  const { curr: loansInstallments, status: loansInstallmentsStatus } = useBatchEditable<BatchInstallmentResp>({
    batchEndpoints: batchInstallmentsObj,
  });

  const loanInfoBox = (loanInfo: SearchResp) => {
    return (
      <>
        <p className={Styles.loantitle}>Loan</p>
        <p className={Styles.loanNo}>{loanInfo.loanNo}</p>
        <p className={Styles.loantitle}>Property Address</p>
        <p className={Styles.propAddr}>{loanInfo.propFullAddress}</p>

        {loansBatch && loansBatch[loanInfo.loanNo] && loansInstallments && loansInstallments[loanInfo.loanNo] ? (
          <>
            <div className={Styles.nextpaymentduediv}>
              <p className={Styles.nextpaymentsection}>
                {'Next payment '}
                <NumericFormat
                  value={loansInstallments[loanInfo.loanNo].borrowerPiti}
                  decimalScale={2}
                  fixedDecimalScale
                  prefix='$'
                  thousandSeparator
                  displayType='text'
                  renderText={(value) => <b>{value}</b>}
                />
                {' due '}
                {<b>{formatDate(epochToDate(addMonthsYrsToTimestamp(loansBatch[loanInfo.loanNo].lpi, 1, 0)))}</b>}
              </p>
            </div>
            <div className={Styles.nextpaymentduemobilediv}>
              <p className={Styles.loantitle}>Next Payment</p>
              <p>
                <NumericFormat
                  value={loansInstallments[loanInfo.loanNo].borrowerPiti}
                  decimalScale={2}
                  fixedDecimalScale
                  prefix='$'
                  thousandSeparator
                  displayType='text'
                />
              </p>
              <p className={Styles.loantitle}>Next Payment Due Date</p>
              <p>{formatDate(epochToDate(addMonthsYrsToTimestamp(loansBatch[loanInfo.loanNo].lpi, 1, 0)))}</p>
            </div>
          </>
        ) : (
          <CircularProgress size='20px'></CircularProgress>
        )}
        <div className={Styles.buttonrow}>
          <button
            className={Styles.primbutton}
            onClick={() => {
              setLoanNumber(loanInfo.loanNo);
              handleNavigation('/make-one-payment');
            }}
          >
            Make a Payment
          </button>
          <button
            className={Styles.secondarybutton}
            onClick={(e) => {
              setLoanNumber(loanInfo.loanNo);
              handleNavigation('/loan-information');
            }}
          >
            Details
          </button>
        </div>
      </>
    );
  };

  const textInformation = () => {
    return (
      !loading && (
        <>
          {!isStrEmptyOrNull(errors) && !isStrEmptyOrNull(email) && (
            <div className={Styles.text}>An error occurred.</div>
          )}
          {isStrEmptyOrNull(errors) && loans.length === 0 && !isStrEmptyOrNull(email) && (
            <div className={Styles.text}>No loans were found.</div>
          )}
          {isStrEmptyOrNull(email) && (
            <div className={Styles.text}>
              Please <a href='/redcarpetservice/login'>Login</a>
            </div>
          )}
        </>
      )
    );
  };

  const loansContainer = () => {
    return (
      <div className={Styles.loanscontainer}>
        {noDuplicateLoans.map((loan: SearchResp) => (
          <div className={Styles.grid} key={loan.loanNo}>
            {loanInfoBox(loan)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {loading && <LoadingModal open={true} />}
      {textInformation()}
      <ThemeProvider theme={theme}>{loansContainer()}</ThemeProvider>
    </>
  );
};

export default BorrowerSelectLoans;
